import * as React from "react";
import { Box, Center, Flex, HStack, IconButton, Input, InputGroup, InputLeftElement, Spinner, Text, VStack } from "@chakra-ui/react";
import { MagnifyingGlass, Plus, X } from "@phosphor-icons/react";
import FilePill from "./FilePill";

// Search + selectable list of existing library files. Visually mirrors the
// drop zone (1.5px dashed lime) so the row swap reads as a mode change, not
// a different component.
function LibraryPicker({ libraryFiles, isLoading, search, onSearchChange, onPick, onClose }) {
  const filtered = libraryFiles?.filter(
    (f) => !search || (f.title || f.filename || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box
      border="1.5px dashed"
      borderColor="rgba(133,194,42,0.4)"
      bg="white"
      borderRadius="10px"
      p="14px"
      mb={2}
    >
      <Flex align="center" gap={2} mb={2}>
        <InputGroup size="sm" flex={1}>
          <InputLeftElement pointerEvents="none" color="gray.400" h="32px">
            <MagnifyingGlass size={12} />
          </InputLeftElement>
          <Input
            placeholder="Search documents…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            type="search"
            borderRadius="999px"
            borderColor="gray.200"
            fontSize="12px"
            _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
          />
        </InputGroup>
        <IconButton
          aria-label="Close library"
          icon={<X size={14} />}
          onClick={onClose}
          variant="ghost"
          size="sm"
          color="gray.400"
        />
      </Flex>

      {isLoading ? (
        <Center py={3}><Spinner size="sm" /></Center>
      ) : !filtered || filtered.length === 0 ? (
        <Text fontSize="12px" color="gray.400" py={2}>
          {search ? "No matching documents." : "No library documents available."}
        </Text>
      ) : (
        <VStack
          spacing={1}
          align="stretch"
          maxH="200px"
          overflowY="auto"
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          {filtered.map((fileInfo) => (
            <HStack
              key={fileInfo.id}
              spacing={3}
              px={2}
              py="6px"
              borderRadius="6px"
              cursor="pointer"
              _hover={{ bg: "gray.50" }}
              onClick={() => onPick(fileInfo)}
            >
              <FilePill fileInfo={fileInfo} size={9} />
              <Text fontSize="12.5px" color="darkTeal" flex={1} noOfLines={1}>
                {fileInfo.title || fileInfo.filename || fileInfo.id}
              </Text>
              <Plus size={12} color="var(--chakra-colors-limeShade-600)" />
            </HStack>
          ))}
        </VStack>
      )}
    </Box>
  );
}

export default LibraryPicker;
